import { useState, useCallback, useEffect, useRef } from 'react';
import { submitQuery, getSummary } from '../services/api';
import { ChatMessage, QueryResponse } from '../types';
import ChatInput from '../components/ChatInput';
import ChatWindow from '../components/ChatWindow';
import SyncStatus from '../components/SyncStatus';

/**
 * The question fired automatically once the deterministic summary has posted.
 *
 * Why it exists: `_summary.md` states the facts, and a stakeholder still has to read twelve
 * component rows to learn whether Alpha will land. This asks for the judgement a table cannot
 * carry — on track or not, what lands next, what to worry about.
 *
 * Why the exclusions are so specific: the model never sees `_summary.md`. It answers from
 * retrieved hall chunks, and since the sync started reading hall's YAML stores those chunks
 * include the very rows the summary now tabulates — components, per-component percent, open
 * action items, open issues. Without naming them, the "briefing" restates the table directly
 * above it. **Keep this list in step with the sections built in `lambda/sync/summary.ts`.**
 */
const STAKEHOLDER_BRIEF_QUESTION =
  'You are briefing a business stakeholder who just asked "How is DSS going?". ' +
  'A status board is already on screen above your answer: it lists the active phases and ' +
  'next phase, every component with its owner and RAG status, per-component task counts and ' +
  'percent complete, the team and their leave, all open action items with the overdue ones ' +
  'flagged, all open issues, and the full roadmap. ' +
  'Do NOT restate any of that — no component tables, no status lists, no per-item rundowns, ' +
  'no counts the board already gives. ' +
  'Give the reading of it instead, in markdown: open with a one-line **bottom line** on ' +
  'whether the current phase lands on its end date, then 2-4 bullets covering the next ' +
  'deliverable and its date, and anything a stakeholder should be aware of — a slip that is ' +
  'not obvious from a percentage, a dependency outside the team, a decision waiting on ' +
  'someone. Draw on decisions, scrum notes and handovers for the why. ' +
  'Where the board already shows a number, interpret it rather than repeat it. ' +
  'Keep it short and plain-language: no jargon, no component codes without their plain name.';

/**
 * Main chat page — composes ChatInput, ChatWindow, and SyncStatus.
 * Manages session ID across queries for multi-turn conversations (FR-008).
 * Fetches static summary on mount, then fires background LLM query.
 * "New Chat" button starts a fresh session (FR-009).
 */
export default function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const backgroundQueryFired = useRef(false);

  // Fetch static summary on mount and add as first chat message
  useEffect(() => {
    let cancelled = false;
    getSummary()
      .then(result => {
        if (cancelled) return;
        if (result.summary) {
          const summaryMessage: ChatMessage = {
            id: 'dashboard-summary',
            role: 'assistant',
            content: result.summary,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [summaryMessage, ...prev]);
        }
        if (result.lastSyncTime) {
          setLastSyncTime(result.lastSyncTime);
        }
      })
      .catch(err => {
        console.warn('Failed to fetch summary:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Fire background LLM query after summary message is added
  useEffect(() => {
    if (messages.length === 0 || backgroundQueryFired.current) return;
    if (messages[0]?.id !== 'dashboard-summary') return;
    backgroundQueryFired.current = true;

    const fireBackgroundQuery = async () => {
      setLoading(true);
      try {
        const response: QueryResponse = await submitQuery({
          sessionId,
          question: STAKEHOLDER_BRIEF_QUESTION,
        });

        setSessionId(response.sessionId);
        setLastSyncTime(response.lastSyncTime);

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response.answer,
          citations: response.citations,
          groundingStatus: response.groundingStatus,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      } catch (error) {
        console.warn('Background LLM query failed:', error);
        // Summary message stays in chat history regardless
      } finally {
        setLoading(false);
      }
    };

    fireBackgroundQuery();
  }, [messages, sessionId]);

  const handleSubmit = useCallback(async (question: string) => {
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: question,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const response: QueryResponse = await submitQuery({
        sessionId,
        question,
      });

      setSessionId(response.sessionId);
      setLastSyncTime(response.lastSyncTime);

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.answer,
        citations: response.citations,
        groundingStatus: response.groundingStatus,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        groundingStatus: 'not-found',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setSessionId(undefined);
    setLastSyncTime(null);
    backgroundQueryFired.current = false;

    // Re-fetch summary as first chat message
    getSummary()
      .then(result => {
        if (result.summary) {
          const summaryMessage: ChatMessage = {
            id: 'dashboard-summary',
            role: 'assistant',
            content: result.summary,
            timestamp: new Date().toISOString(),
          };
          setMessages([summaryMessage]);
        }
        if (result.lastSyncTime) {
          setLastSyncTime(result.lastSyncTime);
        }
      })
      .catch(err => {
        console.warn('Failed to fetch summary:', err);
      });
  }, []);

  return (
    <div className="chat-layout">
      <div className="chat-toolbar">
        <SyncStatus lastSyncTime={lastSyncTime} />
        <button onClick={handleNewChat} className="new-chat-btn">New Chat</button>
      </div>
      <ChatWindow messages={messages} loading={loading} />
      <ChatInput onSubmit={handleSubmit} disabled={loading} />
    </div>
  );
}
